import React, { useState, useRef } from 'react';
import { Form, Button, Row, Col, Alert, Badge, Tabs, Tab, Spinner } from 'react-bootstrap';
import { FaFileUpload, FaExclamationTriangle, FaCheck, FaTimesCircle, FaCloudUploadAlt, FaSalesforce } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

// Custom styles for form labels
const labelStyle = {
  fontWeight: '600',
  color: '#495057',
  marginBottom: '0.25rem',
  fontSize: '0.9rem'
};

// Styles for the drop zone
const dropZoneStyle = {
  border: '2px dashed #ccc',
  borderRadius: '8px',
  padding: '15px',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: '#f8f9fa',
  transition: 'all 0.3s ease'
};

const activeDropZoneStyle = {
  ...dropZoneStyle,
  border: '2px dashed #0d6efd',
  backgroundColor: 'rgba(13, 110, 253, 0.05)'
};

const successDropZoneStyle = {
  ...dropZoneStyle,
  border: '2px dashed #198754',
  backgroundColor: 'rgba(25, 135, 84, 0.05)'
};

// Compact button styles
const tinyButton = {
  padding: '0.1rem 0.3rem',
  fontSize: '0.7rem',
  lineHeight: '1',
  borderRadius: '0.2rem',
  margin: '0 0.05rem',
  minWidth: '3.2rem'
};

// Remove button style - solid red with centered icon
const removeButtonStyle = {
  padding: '0.1rem 0.3rem',
  fontSize: '0.75rem',
  lineHeight: '1',
  borderRadius: '0.2rem',
  margin: '0 0.1rem',
  minWidth: '2.2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

// File item styles
const fileItemStyle = {
  padding: '0.25rem 0.5rem', 
  fontSize: '0.85rem',
  marginBottom: '0.2rem',
  border: 'none',
  borderBottom: '1px solid #efefef',
  display: 'flex',
  alignItems: 'center'
};

// File name container style with proper spacing
const fileNameStyle = {
  flex: '1 1 0%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  minWidth: '0'
};

// Connection dots style
const dotsConnectorStyle = {
  flex: '1 1 auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: '#aaa',
  fontSize: '0.8em',
  letterSpacing: '2px',
  margin: '0 5px'
};

// Buttons container with fixed width and alignment
const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  width: '100px',
  marginRight: '8px',
  flexShrink: 0
};

// Section heading styles
const sectionHeadingStyle = {
  fontSize: '0.9rem',
  fontWeight: '600',
  marginBottom: '0.3rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

// File list container
const fileListContainerStyle = {
  maxHeight: '200px',
  overflowY: 'auto',
  padding: '0.25rem',
  marginBottom: '0.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '0.25rem'
};

// Equipment financing specific file item style (same font size, just more compact padding)
const equipmentFileItemStyle = {
  ...fileItemStyle,
  padding: '0.2rem 0.3rem'
};

// Equipment financing specific button container (wider for 3 full-size buttons)
const equipmentButtonContainerStyle = {
  ...buttonContainerStyle,
  width: '160px',
  marginRight: '8px'
};

// Equipment financing specific file name style (same font size)
const equipmentFileNameStyle = {
  ...fileNameStyle
};

// Equipment financing specific tiny button style (same size as working capital)
const equipmentTinyButton = {
  ...tinyButton
};

/**
 * FileUploads Component
 * 
 * Handles file uploads for bank statements and application documents.
 * Supports both local file uploads and selecting files from Salesforce.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.bankFiles - Array of bank statement files
 * @param {Function} props.setBankFiles - Function to update bank files
 * @param {Array} props.appFiles - Array of application files
 * @param {Function} props.setAppFiles - Function to update app files
 * @param {Array} props.quoteFiles - Array of quote files for equipment financing
 * @param {Function} props.setQuoteFiles - Function to update quote files
 * @param {Array} props.creditFiles - Array of credit files for equipment financing
 * @param {Function} props.setCreditFiles - Function to update credit files
 * @param {boolean} props.disabled - Whether upload is disabled
 * @param {boolean} props.submitting - Whether submission is in progress
 * @param {Array} props.salesforceFiles - Pre-loaded Salesforce files
 * @param {boolean} props.loadingSfFiles - Whether Salesforce files are loading
 * @param {string|null} props.sfFilesError - Error message if Salesforce files failed to load
 * @param {string} props.financingType - Current financing type ('working-capital' or 'equipment-financing')
 * @returns {JSX.Element} FileUploads component
 */
const FileUploads = ({ 
  bankFiles = [], 
  setBankFiles, 
  appFiles = [], 
  setAppFiles,
  quoteFiles = [],
  setQuoteFiles,
  creditFiles = [],
  setCreditFiles,
  disabled = false, 
  submitting = false,
  salesforceFiles = [],
  loadingSfFiles = false,
  sfFilesError = null,
  financingType = 'working-capital'
}) => {
  const [activeDragZone, setActiveDragZone] = useState(null);
  const bankInputRef = useRef(null);
  const appInputRef = useRef(null);
  const quoteInputRef = useRef(null);
  const creditInputRef = useRef(null);
  const { opportunityId } = useParams();
  
  // State for active tab - defaulting to 'upload' since Salesforce tab is hidden
  // NOTE: Change back to 'salesforce' if re-enabling the Salesforce Files tab
  const [activeTab, setActiveTab] = useState('upload');
  
  /**
   * Handle file selection for bank statements or application documents
   * 
   * @param {Event} e - Change event from file input
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const handleFileChange = (e, fileType) => {
    const files = Array.from(e.target.files);
    addFiles(files, fileType);
  };

  /**
   * Add files to the appropriate collection
   * 
   * @param {Array} files - Files to add
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const addFiles = (files, fileType) => {
    if (disabled || submitting) return;
    
    if (fileType === 'bank') {
      setBankFiles(prevFiles => [...prevFiles, ...files]);
    } else if (fileType === 'app') {
      setAppFiles(prevFiles => [...prevFiles, ...files]);
    } else if (fileType === 'quote') {
      setQuoteFiles(prevFiles => [...prevFiles, ...files]);
    } else if (fileType === 'credit') {
      setCreditFiles(prevFiles => [...prevFiles, ...files]);
    }
  };

  /**
   * Remove a file from the selected files list
   * 
   * @param {string} fileType - Type of file ('bank' or 'app')
   * @param {number} index - Index of the file to remove
   */
  const handleRemoveFile = (fileType, index) => {
    if (fileType === 'bank') {
      setBankFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    } else if (fileType === 'app') {
      setAppFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    } else if (fileType === 'quote') {
      setQuoteFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    } else if (fileType === 'credit') {
      setCreditFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    }
  };
  
  /**
   * Select a Salesforce file for use
   * 
   * @param {Object} file - Salesforce file object
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const handleSelectSalesforceFile = async (file, fileType) => {
    if (disabled || submitting) return;
    
    try {
      // Check if the file is an Attachment or ContentDocument
      const isAttachment = file.source === 'Attachment';
      
      // Fix filename to prevent duplicate extensions
      let fileName = file.name || '';
      const extension = file.extension ? `.${file.extension}` : '';
      
      // If the name already ends with the extension, don't add it again
      if (!fileName.toLowerCase().endsWith(extension.toLowerCase())) {
        fileName = fileName + extension;
      }
      
      // Create a File object similar to what would be selected through file input
      // The file will be downloaded when actually submitting to the lender
      const fileObj = {
        name: isAttachment ? file.fullName : fileName,
        size: file.size || 0,
        type: file.contentType || 'application/octet-stream',
        // Add Salesforce metadata to identify this as a Salesforce file
        salesforceFile: true,
        salesforceId: file.id,
        source: file.source || 'ContentDocument', // Track the source type
        // This will be populated later when actually submitting
        content: null
      };
      
      if (fileType === 'bank') {
        setBankFiles(prevFiles => [...prevFiles, fileObj]);
      } else if (fileType === 'app') {
        setAppFiles(prevFiles => [...prevFiles, fileObj]);
      } else if (fileType === 'quote') {
        setQuoteFiles(prevFiles => [...prevFiles, fileObj]);
      } else if (fileType === 'credit') {
        setCreditFiles(prevFiles => [...prevFiles, fileObj]);
      }
    } catch (error) {
      console.error('Error selecting Salesforce file:', error);
      // Remove the error message setting since we can't modify the props
      // Just log the error to console instead
    }
  };

  /**
   * Handle drag over event for drop zone
   * 
   * @param {Event} e - Drag event
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const handleDragOver = (e, fileType) => {
    e.preventDefault();
    if (disabled || submitting) return;
    setActiveDragZone(fileType);
  };

  /**
   * Handle drag leave event for drop zone
   * 
   * @param {Event} e - Drag event
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    setActiveDragZone(null);
  };

  /**
   * Handle drop event for drop zone
   * 
   * @param {Event} e - Drop event
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const handleDrop = (e, fileType) => {
    e.preventDefault();
    if (disabled || submitting) return;
    
    setActiveDragZone(null);
    
    // Check if files are dropped
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      addFiles(files, fileType);
    }
  };

  /**
   * Handle click on the drop zone to open file browser
   * 
   * @param {string} fileType - Type of file ('bank', 'app', 'quote', or 'credit')
   */
  const handleDropZoneClick = (fileType) => {
    if (disabled || submitting) return;
    
    if (fileType === 'bank' && bankInputRef.current) {
      bankInputRef.current.click();
    } else if (fileType === 'app' && appInputRef.current) {
      appInputRef.current.click();
    } else if (fileType === 'quote' && quoteInputRef.current) {
      quoteInputRef.current.click();
    } else if (fileType === 'credit' && creditInputRef.current) {
      creditInputRef.current.click();
    } else if (fileType === 'quote') {
      document.getElementById('quote-file-input').click();
    } else if (fileType === 'credit') {
      document.getElementById('credit-file-input').click();
    }
  };

  const bankUploaded = bankFiles.length > 0;
  const appUploaded = appFiles.length > 0;
  const quoteUploaded = quoteFiles.length > 0;
  const creditUploaded = creditFiles.length > 0;
  
  // Check if all required files are uploaded based on financing type
  const isFileRequirementMet = financingType === 'equipment-financing' 
    ? (appUploaded && quoteUploaded && creditUploaded) 
    : (bankUploaded && appUploaded);
  
  // Calculate total uploaded based on financing type
  const totalUploaded = financingType === 'equipment-financing'
    ? (appUploaded ? 1 : 0) + (quoteUploaded ? 1 : 0) + (creditUploaded ? 1 : 0)
    : (bankUploaded ? 1 : 0) + (appUploaded ? 1 : 0);

  // Determine drop zone style based on state
  const getBankDropZoneStyle = () => {
    if (bankUploaded) return successDropZoneStyle;
    if (activeDragZone === 'bank') return activeDropZoneStyle;
    return dropZoneStyle;
  };

  const getAppDropZoneStyle = () => {
    if (appUploaded) return successDropZoneStyle;
    if (activeDragZone === 'app') return activeDropZoneStyle;
    return dropZoneStyle;
  };
  
  // Determine file extension to help categorize files
  const getFileCategory = (file) => {
    const extension = file.extension ? file.extension.toLowerCase() : '';
    
    // Common bank statement file extensions
    const bankExtensions = ['pdf', 'csv', 'xls', 'xlsx', 'doc', 'docx'];
    // Common application file extensions
    const appExtensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    
    // Check filename for keywords
    const filename = file.name ? file.name.toLowerCase() : '';
    
    if (filename.includes('bank') || 
        filename.includes('statement') || 
        filename.includes('account')) {
      return 'bank';
    }
    
    if (filename.includes('app') || 
        filename.includes('application') || 
        filename.includes('form')) {
      return 'app';
    }
    
    // Default categorization based on extension
    if (bankExtensions.includes(extension)) {
      return 'bank';
    }
    
    return 'app';
  };

  // Helper to render selected files
  const renderSelectedFiles = (fileType, files) => {
    // Use equipment-specific styles for equipment financing layout
    const isEquipmentFinancing = financingType === 'equipment-financing';
    const currentFileItemStyle = isEquipmentFinancing ? equipmentFileItemStyle : fileItemStyle;
    const currentButtonContainerStyle = isEquipmentFinancing ? equipmentButtonContainerStyle : buttonContainerStyle;
    const currentFileNameStyle = isEquipmentFinancing ? equipmentFileNameStyle : fileNameStyle;
    
    return (
      <div style={fileListContainerStyle}>
        {files.length === 0 ? (
          <div className="text-muted py-1 text-left" style={{fontSize: '0.8rem', paddingLeft: '8px'}}>
            No {fileType === 'bank' ? 'bank statements' : fileType === 'app' ? 'application documents' : fileType === 'quote' ? 'quote files' : 'credit files'} selected
          </div>
        ) : (
          files.map((file, index) => {
            // Check if it's a Salesforce file and determine its source
            const isSalesforceFile = file.salesforceFile === true;
            const isAttachment = file.source === 'Attachment';
            
            return (
              <div key={index} style={currentFileItemStyle}>
                <div style={currentButtonContainerStyle}>
                  <Button 
                    variant="danger" 
                    size="sm" 
                    onClick={() => handleRemoveFile(fileType, index)}
                    disabled={submitting || disabled}
                    style={removeButtonStyle}
                  >
                    <FaTimesCircle size="0.8em" />
                  </Button>
                </div>
                <div style={currentFileNameStyle}>
                  {isSalesforceFile && (
                    isAttachment ? 
                      <FaSalesforce className="me-1 text-success" size="0.8em" title="Attachment" /> :
                      <FaSalesforce className="me-1 text-primary" size="0.8em" title="File" />
                  )}
                  <small>{file.name} <span className="text-muted">({Math.round(file.size/1024)}KB)</span></small>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // Render the file upload UI
  const renderFileUploadTab = () => {
    if (financingType === 'equipment-financing') {
      // For equipment financing: application, quotes, credit
      return (
        <div className="equipment-financing-uploads">
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>
                  Application Documents <span className="text-danger">*</span>
                  {appUploaded ? (
                    <Badge bg="success" className="ms-2" pill>
                      <FaCheck className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>{appFiles.length}</span>
                    </Badge>
                  ) : (
                    <Badge bg="danger" className="ms-2" pill>
                      <FaTimesCircle className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>Required</span>
                    </Badge>
                  )}
                </Form.Label>
                
                {/* Hidden file input */}
                <Form.Control
                  ref={appInputRef}
                  type="file"
                  multiple
                  onChange={e => handleFileChange(e, 'app')}
                  disabled={submitting || disabled}
                  className="d-none"
                  id="app-file-input"
                />
                
                {/* Drop zone */}
                <div
                  style={getAppDropZoneStyle()}
                  onDragOver={e => handleDragOver(e, 'app')}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, 'app')}
                  onClick={() => handleDropZoneClick('app')}
                  className={`mb-3 file-drop-zone ${activeDragZone === 'app' ? 'active' : ''}`}
                >
                  <FaCloudUploadAlt size={24} className="mb-1" color={appUploaded ? "#198754" : "#0d6efd"} />
                  <div>
                    <strong>{appUploaded ? "Add More Application Documents" : "Upload Application Documents"}</strong>
                  </div>
                  <div className="text-muted" style={{fontSize: '0.8rem'}}>
                    Drag & drop files here or click to browse
                  </div>
                </div>

                {/* List of uploaded application files */}
                {appFiles.length > 0 && renderSelectedFiles('app', appFiles)}
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>
                  Quotes <span className="text-danger">*</span>
                  {quoteUploaded ? (
                    <Badge bg="success" className="ms-2" pill>
                      <FaCheck className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>{quoteFiles.length}</span>
                    </Badge>
                  ) : (
                    <Badge bg="danger" className="ms-2" pill>
                      <FaTimesCircle className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>Required</span>
                    </Badge>
                  )}
                </Form.Label>
                
                {/* Hidden file input */}
                <Form.Control
                  type="file"
                  multiple
                  ref={quoteInputRef}
                  onChange={e => handleFileChange(e, 'quote')}
                  disabled={submitting || disabled}
                  className="d-none"
                  id="quote-file-input"
                />
                
                {/* Drop zone */}
                <div
                  style={quoteUploaded ? successDropZoneStyle : (activeDragZone === 'quote' ? activeDropZoneStyle : dropZoneStyle)}
                  onDragOver={e => handleDragOver(e, 'quote')}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, 'quote')}
                  onClick={() => handleDropZoneClick('quote')}
                  className={`mb-3 file-drop-zone ${activeDragZone === 'quote' ? 'active' : ''}`}
                >
                  <FaCloudUploadAlt size={24} className="mb-1" color={quoteUploaded ? "#198754" : "#0d6efd"} />
                  <div>
                    <strong>{quoteUploaded ? "Add More Quotes" : "Upload Quotes"}</strong>
                  </div>
                  <div className="text-muted" style={{fontSize: '0.8rem'}}>
                    Drag & drop files here or click to browse
                  </div>
                </div>

                {/* List of uploaded quote files */}
                {quoteFiles.length > 0 && renderSelectedFiles('quote', quoteFiles)}
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>
                  Credit Documents <span className="text-danger">*</span>
                  {creditUploaded ? (
                    <Badge bg="success" className="ms-2" pill>
                      <FaCheck className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>{creditFiles.length}</span>
                    </Badge>
                  ) : (
                    <Badge bg="danger" className="ms-2" pill>
                      <FaTimesCircle className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>Required</span>
                    </Badge>
                  )}
                </Form.Label>
                
                {/* Hidden file input */}
                <Form.Control
                  ref={creditInputRef}
                  type="file"
                  multiple
                  onChange={e => handleFileChange(e, 'credit')}
                  disabled={submitting || disabled}
                  className="d-none"
                  id="credit-file-input"
                />
                
                {/* Drop zone */}
                <div
                  style={creditUploaded ? successDropZoneStyle : (activeDragZone === 'credit' ? activeDropZoneStyle : dropZoneStyle)}
                  onDragOver={e => handleDragOver(e, 'credit')}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, 'credit')}
                  onClick={() => handleDropZoneClick('credit')}
                  className={`mb-3 file-drop-zone ${activeDragZone === 'credit' ? 'active' : ''}`}
                >
                  <FaCloudUploadAlt size={24} className="mb-1" color={creditUploaded ? "#198754" : "#0d6efd"} />
                  <div>
                    <strong>{creditUploaded ? "Add More Credit Documents" : "Upload Credit Documents"}</strong>
                  </div>
                  <div className="text-muted" style={{fontSize: '0.8rem'}}>
                    Drag & drop files here or click to browse
                  </div>
                </div>

                {/* List of uploaded credit files */}
                {creditFiles.length > 0 && renderSelectedFiles('credit', creditFiles)}
              </Form.Group>
            </Col>
            <Col md={6}>
              {/* Intentionally left empty for symmetry */}
            </Col>
          </Row>
        </div>
      );
    } else {
      // For working capital: bank statements and applications (original UI)
      return (
        <div className="local-files-section">
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>
                  Bank Statements <span className="text-danger">*</span>
                  {bankUploaded ? (
                    <Badge bg="success" className="ms-2" pill>
                      <FaCheck className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>{bankFiles.length}</span>
                    </Badge>
                  ) : (
                    <Badge bg="danger" className="ms-2" pill>
                      <FaTimesCircle className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>Required</span>
                    </Badge>
                  )}
                </Form.Label>
                
                {/* Hidden file input */}
                <Form.Control
                  ref={bankInputRef}
                  type="file"
                  multiple
                  onChange={e => handleFileChange(e, 'bank')}
                  disabled={submitting || disabled}
                  className="d-none"
                  id="bank-file-input"
                />
                
                {/* Drop zone */}
                <div
                  style={getBankDropZoneStyle()}
                  onDragOver={e => handleDragOver(e, 'bank')}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, 'bank')}
                  onClick={() => handleDropZoneClick('bank')}
                  className={`mb-3 file-drop-zone ${activeDragZone === 'bank' ? 'active' : ''}`}
                >
                  <FaCloudUploadAlt size={24} className="mb-1" color={bankUploaded ? "#198754" : "#0d6efd"} />
                  <div>
                    <strong>{bankUploaded ? "Add More Bank Statements" : "Upload Bank Statements"}</strong>
                  </div>
                  <div className="text-muted" style={{fontSize: '0.8rem'}}>
                    Drag & drop files here or click to browse
                  </div>
                </div>

                {/* List of uploaded bank files */}
                {bankFiles.length > 0 && renderSelectedFiles('bank', bankFiles)}
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>
                  Application Documents <span className="text-danger">*</span>
                  {appUploaded ? (
                    <Badge bg="success" className="ms-2" pill>
                      <FaCheck className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>{appFiles.length}</span>
                    </Badge>
                  ) : (
                    <Badge bg="danger" className="ms-2" pill>
                      <FaTimesCircle className="me-1" size="0.7em" />
                      <span style={{fontSize: '0.8rem'}}>Required</span>
                    </Badge>
                  )}
                </Form.Label>
                
                {/* Hidden file input */}
                <Form.Control
                  ref={appInputRef}
                  type="file"
                  multiple
                  onChange={e => handleFileChange(e, 'app')}
                  disabled={submitting || disabled}
                  className="d-none"
                  id="app-file-input"
                />
                
                {/* Drop zone */}
                <div
                  style={getAppDropZoneStyle()}
                  onDragOver={e => handleDragOver(e, 'app')}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, 'app')}
                  onClick={() => handleDropZoneClick('app')}
                  className={`mb-3 file-drop-zone ${activeDragZone === 'app' ? 'active' : ''}`}
                >
                  <FaCloudUploadAlt size={24} className="mb-1" color={appUploaded ? "#198754" : "#0d6efd"} />
                  <div>
                    <strong>{appUploaded ? "Add More Application Documents" : "Upload Application Documents"}</strong>
                  </div>
                  <div className="text-muted" style={{fontSize: '0.8rem'}}>
                    Drag & drop files here or click to browse
                  </div>
                </div>

                {/* List of uploaded application files */}
                {appFiles.length > 0 && renderSelectedFiles('app', appFiles)}
              </Form.Group>
            </Col>
          </Row>
        </div>
      );
    }
  };
  
  // Render the Salesforce files tab
  const renderSalesforceFilesTab = () => {
    const showEquipmentFinancingOptions = financingType === 'equipment-financing';
    // Use equipment-specific styles for equipment financing layout
    const isEquipmentFinancing = financingType === 'equipment-financing';
    const currentFileItemStyle = isEquipmentFinancing ? equipmentFileItemStyle : fileItemStyle;
    const currentButtonContainerStyle = isEquipmentFinancing ? equipmentButtonContainerStyle : buttonContainerStyle;
    const currentFileNameStyle = isEquipmentFinancing ? equipmentFileNameStyle : fileNameStyle;
    const currentTinyButtonStyle = isEquipmentFinancing ? equipmentTinyButton : tinyButton;
    
    return (
      <div className="salesforce-files-section">
        {loadingSfFiles ? (
          <div className="text-left py-2">
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="mt-1" style={{fontSize: '0.85rem'}}>Loading files from Salesforce...</p>
          </div>
        ) : sfFilesError ? (
          <Alert variant="danger" className="py-1 px-3 mb-2">
            <FaExclamationTriangle className="me-1" size="0.8em" />
            <span style={{fontSize: '0.85rem'}}>{sfFilesError}</span>
          </Alert>
        ) : salesforceFiles.length === 0 ? (
          <Alert variant="info" className="py-1 px-3 mb-2">
            <span style={{fontSize: '0.85rem'}}>No files found in Salesforce for this opportunity.</span>
          </Alert>
        ) : (
          <>
            {/* Available Salesforce Files */}
            <div style={sectionHeadingStyle}>
              <span>Available Salesforce Files</span>
              <span className="text-muted small">({salesforceFiles.length})</span>
            </div>
            
            <div style={{...fileListContainerStyle, maxHeight: '300px'}}>
              {salesforceFiles.map((file, index) => {
                const fileCategory = getFileCategory(file);
                // Determine icon based on file source
                const isAttachment = file.source === 'Attachment';
                
                return (
                  <div key={file.id || index} style={currentFileItemStyle}>
                    <div style={currentButtonContainerStyle}>
                      {!showEquipmentFinancingOptions ? (
                        // Working Capital options
                        <>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSelectSalesforceFile(file, 'bank')}
                            disabled={disabled || submitting}
                            style={currentTinyButtonStyle}
                          >
                            Bank
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSelectSalesforceFile(file, 'app')}
                            disabled={disabled || submitting}
                            style={currentTinyButtonStyle}
                          >
                            App
                          </Button>
                        </>
                      ) : (
                        // Equipment Financing options
                        <>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSelectSalesforceFile(file, 'app')}
                            disabled={disabled || submitting}
                            style={currentTinyButtonStyle}
                          >
                            App
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSelectSalesforceFile(file, 'quote')}
                            disabled={disabled || submitting}
                            style={currentTinyButtonStyle}
                          >
                            Quote
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSelectSalesforceFile(file, 'credit')}
                            disabled={disabled || submitting}
                            style={currentTinyButtonStyle}
                          >
                            Credit
                          </Button>
                        </>
                      )}
                    </div>
                    <div style={currentFileNameStyle}>
                      {isAttachment ? (
                        <FaSalesforce className="me-1 text-success" size="0.8em" title="Attachment" />
                      ) : (
                        <FaSalesforce className="me-1 text-primary" size="0.8em" title="File" />
                      )}
                      <small className="text-truncate">
                        {file.name}
                        {file.extension && <span className="text-muted">.{file.extension}</span>}
                        <span className="text-muted ms-1">({Math.round(file.size/1024)}KB)</span>
                      </small>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Selected Files */}
            <div className="mt-3">
              <div style={sectionHeadingStyle}>
                <div className="d-flex">
                  {!showEquipmentFinancingOptions ? (
                    // Working Capital badge indicators
                    <>
                      <Badge bg={bankUploaded ? "success" : "danger"} pill className="me-1">
                        <span style={{fontSize: '0.7rem'}}>Bank: {bankFiles.length}</span>
                      </Badge>
                      <Badge bg={appUploaded ? "success" : "danger"} pill>
                        <span style={{fontSize: '0.7rem'}}>App: {appFiles.length}</span>
                      </Badge>
                    </>
                  ) : (
                    // Equipment Financing badge indicators
                    <>
                      <Badge bg={appUploaded ? "success" : "danger"} pill className="me-1">
                        <span style={{fontSize: '0.7rem'}}>App: {appFiles.length}</span>
                      </Badge>
                      <Badge bg={quoteUploaded ? "success" : "danger"} pill className="me-1">
                        <span style={{fontSize: '0.7rem'}}>Quote: {quoteFiles.length}</span>
                      </Badge>
                      <Badge bg={creditUploaded ? "success" : "danger"} pill>
                        <span style={{fontSize: '0.7rem'}}>Credit: {creditFiles.length}</span>
                      </Badge>
                    </>
                  )}
                </div>
              </div>
              
              {!showEquipmentFinancingOptions && (
                <>
                  <div style={{...sectionHeadingStyle, justifyContent: 'flex-start'}} className="mt-2 mb-0">
                    <small>Bank Statements</small>
                  </div>
                  {renderSelectedFiles('bank', bankFiles.filter(file => file.salesforceFile))}
                </>
              )}
              
              <div style={{...sectionHeadingStyle, justifyContent: 'flex-start'}} className="mt-1 mb-0">
                <small>Application Documents</small>
              </div>
              {renderSelectedFiles('app', appFiles.filter(file => file.salesforceFile))}
              
              {showEquipmentFinancingOptions && (
                <>
                  <div style={{...sectionHeadingStyle, justifyContent: 'flex-start'}} className="mt-1 mb-0">
                    <small>Quote Documents</small>
                  </div>
                  {renderSelectedFiles('quote', quoteFiles.filter(file => file.salesforceFile))}
                  
                  <div style={{...sectionHeadingStyle, justifyContent: 'flex-start'}} className="mt-1 mb-0">
                    <small>Credit Documents</small>
                  </div>
                  {renderSelectedFiles('credit', creditFiles.filter(file => file.salesforceFile))}
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="file-uploads-container">
      <div className="section-header mb-2">
        <h5 className="section-title">
          File Uploads 
        </h5>
      </div>

      <div className="section-description mb-2" style={{fontSize: '0.9rem'}}>
        {financingType === 'equipment-financing' ? (
          <span>Please upload application documents, quotes, and credit information.</span>
        ) : (
          <span>Please upload at least one bank statement and one application document.</span>
        )}
      </div>

      {!isFileRequirementMet && (
        <Alert variant="warning" className="py-1 px-3 mb-2">
          <FaExclamationTriangle className="me-2" size="0.8em" />
          <span style={{fontSize: '0.85rem'}}><strong>
            {financingType === 'equipment-financing' ? (
              <>All file types (application, quotes, and credit documents) are required.</>
            ) : (
              <>Both bank statements and application documents are required.</>
            )}
          </strong></span>
        </Alert>
      )}
      
      {isFileRequirementMet && (
        <Alert variant="success" className="py-1 px-3 mb-2">
          <FaCheck className="me-2" size="0.8em" />
          <span style={{fontSize: '0.85rem'}}><strong>All required files uploaded!</strong></span>
        </Alert>
      )}
      
      {/* File upload section - Salesforce tab hidden for now */}
      {/* NOTE: To restore Salesforce Files tab, uncomment the Tabs component below and remove the direct renderFileUploadTab() call */}
      {renderFileUploadTab()}
      
      {/* COMMENTED OUT: Salesforce Files tab - restore this when CRM integration is needed
      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key)}
        className="mb-2"
      >
        <Tab eventKey="salesforce" title={
          <>
            <FaSalesforce className="me-1" size="0.8em" /> <span style={{fontSize: '0.85rem'}}>Salesforce Files</span>
            {loadingSfFiles && <Spinner animation="border" size="sm" className="ms-1" />}
          </>
        }>
          {renderSalesforceFilesTab()}
        </Tab>
        <Tab eventKey="upload" title={
          <span style={{fontSize: '0.85rem'}}>Upload Files Manually</span>
        }>
          {renderFileUploadTab()}
        </Tab>
      </Tabs>
      END COMMENTED OUT SALESFORCE TAB */}
    </div>
  );
};

FileUploads.propTypes = {
  bankFiles: PropTypes.array,
  setBankFiles: PropTypes.func.isRequired,
  appFiles: PropTypes.array,
  setAppFiles: PropTypes.func.isRequired,
  quoteFiles: PropTypes.array,
  setQuoteFiles: PropTypes.func,
  creditFiles: PropTypes.array,
  setCreditFiles: PropTypes.func,
  disabled: PropTypes.bool,
  submitting: PropTypes.bool,
  salesforceFiles: PropTypes.array,
  loadingSfFiles: PropTypes.bool,
  sfFilesError: PropTypes.string,
  financingType: PropTypes.string
};

export default FileUploads; 